import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useEndpointProvider } from "./EndpointProvider";
import { useAuthProvider } from "./AuthProvider";
import { useBackendLinksProvider } from "./BackendLinksProvider";
import { HarmonizedMedication, EMRData } from "../Managers/VisualizationManager/ToolBar/Modals/MedicationGraphModalities/Medications";
import { useEnvironmentVariablesProvider } from "./EnvironmentVariablesProvider";
import { useRecoilState } from "recoil";
import { currentPatientFileInfoAtom, PatientMedications } from "../Pages/Data/Visualize/DataReview/Atoms/PatientFile";

type EMRDataContextType = {
    harmonizedMedications: HarmonizedMedication[]
    emrData: EMRData[]
    updateMedicationList: () => Promise<any>
    updatePatientMedications: (mrn: string) => Promise<PatientMedications>
    updateEMRData: (mrn: string, harmonizedMedications: HarmonizedMedication[]) => Promise<any>
}

const EMRDataContext = createContext<EMRDataContextType>({} as EMRDataContextType)

export const EMRDataProvider: React.FC = ({ children }) => {
    const { environmentVariables } = useEnvironmentVariablesProvider()
    const [harmonizedMedications, setHarmonizedMedications] = useState<HarmonizedMedication[]>([])
    const [medicationData, setMedicationData] = useState<EMRData[]>([])
    const { LINKS } = useBackendLinksProvider()
    const authProvider = useAuthProvider()
    const endpointProvider = useEndpointProvider()
    const [{ patientIdentifier, mrnError }, setCurrentPatientFileInfo] = useRecoilState(currentPatientFileInfoAtom) 
    const previousPatientIdentifier = useRef(patientIdentifier)

    const updateMedicationList = useCallback(() => {
        if (!environmentVariables.FEATURE_FLAG_ADT_INTEGRATION) {
            return new Promise(resolve => resolve(null))
        }

        return endpointProvider.post(LINKS.VISUALIZE.GET_ALL_HARMONIZED_MEDICATIONS).then(medications => {
            setHarmonizedMedications(medications)
            return medications
        })
    }, [LINKS.VISUALIZE.GET_ALL_HARMONIZED_MEDICATIONS, endpointProvider, environmentVariables.FEATURE_FLAG_ADT_INTEGRATION])

    const updateEMRData = useCallback((mrn: string, harmonizedMedications: HarmonizedMedication[]) => {
        if (!environmentVariables.FEATURE_FLAG_ADT_INTEGRATION) {
            return new Promise(resolve => resolve(null))
        }

        return endpointProvider.post(LINKS.VISUALIZE.GET_EMR_DATA, { mrn, harmonizedMedications }).then(data => {
            setMedicationData(data)
            return data
        })
    }, [LINKS.VISUALIZE.GET_EMR_DATA, endpointProvider, environmentVariables.FEATURE_FLAG_ADT_INTEGRATION])

    const updatePatientMedications = useCallback((mrn: string) => {
        if (!environmentVariables.FEATURE_FLAG_ADT_INTEGRATION) {
            return new Promise(resolve => resolve(null))
        }

        return endpointProvider.post(LINKS.VISUALIZE.GET_PATIENT_HARMONIZED_MEDICATIONS, { mrn }).then(async result => {
            setCurrentPatientFileInfo(previous => ({ ...previous, patientMedications: result }))
            await updateEMRData(mrn, result.harmonized)
            return result
        })
    }, [LINKS.VISUALIZE.GET_PATIENT_HARMONIZED_MEDICATIONS, endpointProvider, environmentVariables.FEATURE_FLAG_ADT_INTEGRATION, setCurrentPatientFileInfo, updateEMRData])

    // Make sure we don't persist any data between logins.
    useEffect(() => {
        if (!authProvider.userIsLoggedIn) {
            if (harmonizedMedications.length > 0) {
                setHarmonizedMedications([])
            }

            if (medicationData.length > 0) {
                setMedicationData([])
            }
        }
    }, [authProvider, medicationData, harmonizedMedications.length, environmentVariables.FEATURE_FLAG_ADT_INTEGRATION])

    // When the patient identifier changes, update the medication data
    useEffect(() => {
        if (patientIdentifier === previousPatientIdentifier.current && !mrnError) {
            return
        }

        previousPatientIdentifier.current = patientIdentifier

        if (mrnError) {
            setMedicationData([])
            setCurrentPatientFileInfo(previous => ({ ...previous, patientMedications: { unharmonized: [], harmonized: [] }}))
            return
        }

        updatePatientMedications(patientIdentifier)

    }, [LINKS.VISUALIZE.GET_PATIENT_HARMONIZED_MEDICATIONS, endpointProvider, mrnError, patientIdentifier, setCurrentPatientFileInfo, updateMedicationList, updateEMRData, updatePatientMedications])


    return (
        <EMRDataContext.Provider value={{ harmonizedMedications, emrData: medicationData, updateMedicationList, updatePatientMedications, updateEMRData }}>
            {children}
        </EMRDataContext.Provider>
    )
}

export const useEMRDataProvider = () => {
    return useContext(EMRDataContext)
}
